// Certified.js (FINAL – cloud + auth-safe)
import { useState, useEffect } from "react";
import { app, auth, db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import "./Certified.css";
import logo from "./logo.png";
import { useNavigate } from "react-router-dom";

// Firebase Functions
import { getFunctions, httpsCallable } from "firebase/functions";

const Certified = () => {
  const [name, setName] = useState("");
  const [hours, setHours] = useState(0);
  const [badge, setBadge] = useState("");
  const [loading, setLoading] = useState(true);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [authReady, setAuthReady] = useState(false);

  const navigate = useNavigate();

  // 🔹 Wait until Firebase Auth is fully restored
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(() => {
      setAuthReady(true);
    });
    return unsub;
  }, []);

  // 🔹 Fetch user data AFTER auth is ready
  useEffect(() => {
    if (!authReady) return;

    const fetchUserData = async () => {
      const user = auth.currentUser;

      if (!user) {
        setShowLoginPopup(true);
        setLoading(false);
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const data = userSnap.data().profile || {};
          setName(data.name || "Volunteer");
          setHours(Number(data.volunteerHours || 0));
        } else {
          setName("Volunteer");
          setHours(0);
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [authReady]);

  // 🔹 Compute badge (display only)
  useEffect(() => {
    const computeBadge = (h) => {
      if (h >= 100) return "Platinum";
      if (h >= 50) return "Gold";
      if (h >= 10) return "Silver";
      if (h >= 5) return "Bronze";
      return "Member";
    };
    setBadge(computeBadge(hours));
  }, [hours]);

  // 🔹 Call Cloud Function (AUTHENTICATED)
  const requestCertificate = async () => {
    try {
      const functions = getFunctions(app);
      const fn = httpsCallable(functions, "requestCertificate");

      const res = await fn();

      if (res.data?.pdfUrl) {
        window.open(res.data.pdfUrl, "_blank");
      } else {
        alert("Certificate generation failed.");
      }
    } catch (err) {
      console.error("Certificate error:", err);
      alert(err.message || "Could not generate certificate.");
    }
  };

  // Loading UI
  if (loading) {
    return (
      <div className="certified-container">
        <div className="certificate-card">
          <h2>Loading your certificate...</h2>
        </div>
      </div>
    );
  }

  // Login required popup
  if (showLoginPopup) {
    return (
      <div className="login-popup-overlay">
        <div className="login-popup">
          <h2>Login Required</h2>
          <p>You must be logged in to access your certificate.</p>
          <button
            className="login-redirect-btn"
            onClick={() => navigate("/login")}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Main UI
  return (
    <div className="certified-container">
      <div className="certificate-card">
        <img src={logo} alt="Echelon Logo" className="certificate-logo" />

        <h1 className="certificate-title">Certificate of Achievement</h1>
        <p className="certificate-sub">This certifies that</p>

        <h2 className="certificate-name">{name}</h2>

        <p className="certificate-desc">
          has successfully completed <strong>{hours}</strong> volunteer hours and
          earned the <strong>{badge}</strong> badge.
        </p>
            <p className="certificate-desc">
          (This might take a minute to generate.)
        </p>
        <div className="divider"></div>

        <div className="info-section">
          <div className="info-box">
            <span className="info-label">Volunteer Hours</span>
            <span className="info-value">{hours}</span>
            
          </div>
          <div className="info-box">
            <span className="info-label">Badge</span>
            <span className="info-value">{badge}</span>
  
          </div>
        </div>

        <button
          className="download-btn"
          onClick={requestCertificate}
          disabled={!authReady}
        >
          🎓 Download Certificate
        </button>
      </div>
    </div>
  );
};

export default Certified;
